import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Layout, Spin, Row, Col, Card, Table } from 'antd';
import {
	getCurrency,
	collectAllParentsFromTreeData,
	filterTreeData,
	nullSafeGetOrElse,
	removeEmptyChildrenFromTreeData,
	sortTreeData,
} from '../../utils/DataAccessUtils';
import { applyLocalAnalyticsTreeFilters } from '../../utils/AnalyticsTreeFilterUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { withRouter } from 'react-router';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import { clearLocationFilters, updateLocationFilters } from '../../actions/spend_analytics_actions';
import {
	fetchSpendCostsByLocationWithRegions,
	fetchSpendCostsByMonth,
} from '../../thunks/spend_reportings_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import { SpendOverTimeChart } from '../spend_analytics_overview_page/SpendOverTimeChart';
import { SpendBreakdownSection } from '../spend_analytics_overview_page/SpendBreakdownSection';
import {
	flattenSpendCosts,
	getActiveSpendSources,
	SPEND_SOURCES,
} from '../../utils/AnalyticsUtils';
import { buildSpendBreakdownColumns } from '../spend_analytics_overview_page/SpendBreakdownColumns';
import { exportSpendBySourceCsv } from '../../utils/AnalyticsBreakdownExportUtils';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';

require('./SpendAnalyticsOverviewLocationsPage.less');
const { Content } = Layout;

const CSV_TITLE = 'Spend Analytics by Locations';

const SpendAnalyticsOverviewLocationsPage: FC<any> = ({
	fetchSpendData,
	fetchTimeSeriesSpendData,
	analytics,
	saveLocationFilters,
	clearAllLocationFilters,
	accountActivated,
	currentUser,
	userType,
}): React.ReactElement => {
	const [initialLoading, setInitialLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [filteredTreeData, setFilteredTreeData] = useState([]);
	const [locationIds, setLocationIds] = useState([]);
	const [printing, setPrinting] = useState(false);

	useEffect(() => {
		if (analytics.locationFilters.startDate && analytics.locationFilters.endDate) {
			const promise1 = fetchSpendData(analytics.locationFilters);
			const promise2 = fetchTimeSeriesSpendData(analytics.locationFilters);
			if (initialLoading) {
				Promise.all([promise1, promise2]).then(() => setInitialLoading(false));
			}
		}
	}, [analytics.locationFilters]);

	const filterByLocationIds = useCallback(
		(allData) => applyLocalAnalyticsTreeFilters(allData, { selectedIds: locationIds }),
		[locationIds]
	);

	const spendByLocationDisplayData = useMemo(() => {
		const spendByLocation = nullSafeGetOrElse('spendCostsByLocation', analytics, []);

		const displayData = !!search
			? filteredTreeData
			: filterByLocationIds(nullSafeGetOrElse('data', spendByLocation, []));
		return sortTreeData(
			displayData,
			(a, b) =>
				parseFloat(nullSafeGetOrElse('spendCosts.totalSpend', b, 0)) -
				parseFloat(nullSafeGetOrElse('spendCosts.totalSpend', a, 0))
		);
	}, [analytics, filterByLocationIds, filteredTreeData, search]);

	const { spendCostsByLocation, spendCostsByMonth, locationFilters } = analytics;
	const currency = getCurrency({ currentUser });

	const allFieldRows = useMemo(
		() => flattenSpendCosts(spendByLocationDisplayData),
		[spendByLocationDisplayData]
	);
	const activeSources = useMemo(() => getActiveSpendSources(allFieldRows), [allFieldRows]);
	const printExpandedRowKeys = printing
		? collectAllParentsFromTreeData(spendByLocationDisplayData, (node) => node.id)
		: search
		? collectAllParentsFromTreeData(filteredTreeData, (node) => node.id)
		: null;

	const spendByLocationColumns = useMemo(
		() => [
			{
				title: 'Location',
				dataIndex: 'name',
				key: 'name',
				width: '24%',
				render: (text) => (
					<Ellipsis tooltip={true} style={{ display: 'inline' }} length={28}>
						{text}
					</Ellipsis>
				),
			},
			...buildSpendBreakdownColumns({
				activeSources,
				currency,
				userType,
				baseFilters: locationFilters,
				dimensionFilterKey: 'locationIds',
				fromTree: true,
			}),
		],
		[activeSources, currency, userType, locationFilters]
	);

	const onSearch = (e) => {
		const spendByLocation = nullSafeGetOrElse('spendCostsByLocation', analytics, []);

		const searchValue = e.target.value;
		const allData = filterByLocationIds(nullSafeGetOrElse('data', spendByLocation, []));

		setFilteredTreeData(
			!!searchValue
				? filterTreeData(allData, (obj) =>
						obj.name.toLowerCase().includes(searchValue.toLowerCase())
				  )
				: []
		);
		setSearch(searchValue);
	};

	const removeLocationIdsAndUpdateFilter = useCallback(
		(filters) => {
			const { locationIds = [], ...otherFilters } = filters;
			setLocationIds(locationIds);
			saveLocationFilters(otherFilters);
		},
		[saveLocationFilters]
	);

	const handleExportCsv = useCallback(() => {
		exportSpendBySourceCsv({
			title: CSV_TITLE,
			categoryColumnLabel: 'Location',
			rows: allFieldRows,
			activeSources,
			formatValue: (amount) => currency.symbolFormat(amount, 0),
		});
	}, [allFieldRows, activeSources, currency]);

	const handlePrint = useCallback(() => setPrinting(true), []);

	// Expand all tree rows + drop pagination, let the table re-render, then capture the full table.
	useEffect(() => {
		if (!printing) {
			return;
		}
		const timer = setTimeout(() => {
			exportAnalyticsPageAsPdf(
				'.analyticsOverviewLocationsPage',
				'spend-analytics-locations-' + new Date().toISOString().slice(0, 10) + '.pdf'
			).finally(() => setPrinting(false));
		}, 400);
		return () => clearTimeout(timer);
	}, [printing]);

	return (
		<Content
			className="analyticsOverviewLocationsPage"
			style={{ padding: '0 0.5em', position: 'relative' }}
		>
			<div className="analytics-pdf-only" style={{ display: 'none' }}>
				<h2>Spend Analytics – Locations</h2>
			</div>
			{accountActivated ? (
				<div>
					<Row key={1} style={{ margin: '0.5em -8px' }} gutter={16}>
						<Col span={24}>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<AnalyticsControlBar
									updateFilters={removeLocationIdsAndUpdateFilter}
									locationIds={locationIds}
									clearFilters={clearAllLocationFilters}
									problemTypeIds={locationFilters.problemTypeIds}
									brandIds={locationFilters.brandIds}
									supplierFacilityIds={locationFilters.supplierFacilityIds}
									woPriorityIds={locationFilters.woPriorityIds}
									startDate={locationFilters.startDate}
									endDate={locationFilters.endDate}
									isCapex={locationFilters.isCapex}
									isPM={locationFilters.isPM}
									workCreatedAtStartDate={locationFilters.workCreatedAtStartDate}
									workCreatedAtEndDate={locationFilters.workCreatedAtEndDate}
									workCompletedAtStartDate={locationFilters.workCompletedAtStartDate}
									workCompletedAtEndDate={locationFilters.workCompletedAtEndDate}
									areas={locationFilters.areas}
									areaHierarchyNames={locationFilters.areaHierarchyNames}
									showSearch
									search={search}
									onSearch={onSearch}
									showDownload={true}
									onDownload={handleExportCsv}
									showPrint={true}
									onPrint={handlePrint}
									filterConfig={{
										workCreatedAt: true,
										workCompletedAt: true,
										capEx: true,
										isSpendAnalytics: true,
										isPM: true,
										problemTypes: true,
										workOrderPriorities: true,
										[FILTER_CONFIG_NAMES.AREA]: true,
									}}
									searchPlaceholder={'Search Locations'}
								/>
							</div>
						</Col>
					</Row>

					{spendCostsByMonth.loading || initialLoading || spendByLocationDisplayData.length > 0 ? (
						[
							<>
								<Row key={2} style={{ margin: '0.5em -8px' }} gutter={16}>
									<Col span={24}>
										<Card>
											<Spin spinning={spendCostsByLocation.loading || initialLoading}>
												{spendByLocationDisplayData && spendByLocationDisplayData.length > 0 ? (
													<Table
														pagination={printing ? false : undefined}
														columns={spendByLocationColumns}
														rowKey={(el) => el['id']}
														dataSource={removeEmptyChildrenFromTreeData(spendByLocationDisplayData)}
														expandable={{
															...(printExpandedRowKeys && {
																expandedRowKeys: printExpandedRowKeys,
															}),
														}}
													/>
												) : null}
											</Spin>
										</Card>
									</Col>
								</Row>
								<Row>
									<Col span={24}>
										<SpendBreakdownSection
											loading={spendCostsByLocation.loading || initialLoading}
											filters={locationFilters}
											userType={userType}
											currency={currency}
											rows={allFieldRows}
											activeSources={activeSources}
											totalSources={SPEND_SOURCES}
											reportKey="spend_costs_by_location_with_regions"
											normalizeComparisonRows={(rawData) =>
												flattenSpendCosts(
													applyLocalAnalyticsTreeFilters(rawData || [], {
														selectedIds: locationIds,
														search,
													})
												)
											}
											localFilterRevision={`${search}|${locationIds.join(',')}`}
											categoryLabel="Location"
											titleLabel="Spend by Location"
											csvTitle={CSV_TITLE}
											exportValueLabel="Total Spend"
										/>
									</Col>
								</Row>
							</>,

							<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
								<Col span={24}>
									<SpendOverTimeChart
										monthlyData={spendCostsByMonth.data}
										currency={currency}
										loading={spendCostsByMonth.loading || initialLoading}
									/>
								</Col>
							</Row>,
						]
					) : (
						<EmptyState
							height={undefined}
							headline={'No spend found'}
							body={
								<div
									style={{
										maxWidth: 488,
										textAlign: 'center',
									}}
								>
									We didn't find any spend data. Try changing your filters or expanding the selected
									time period.
								</div>
							}
						/>
					)}
				</div>
			) : (
				<EmptyState
					graphic={
						<img
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
							alt="You haven't activated your account yet."
						/>
					}
					headline={"You haven't activated your account yet."}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 560, marginBottom: 16 }}>
								Please check your email. We sent you a note with an activation link.
							</div>
						</div>
					}
				/>
			)}
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	accountActivated: state.session.accountActivated,
	currentUser: state.session.currentUser,
	analytics: state.spend_analytics,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveLocationFilters: (filters) => dispatch(updateLocationFilters(filters)),
	clearAllLocationFilters: (filters) => dispatch(clearLocationFilters()),
	fetchSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(
			fetchSpendCostsByLocationWithRegions(ownProps.userType)(params, filters, pagination, sorting)
		),
	fetchTimeSeriesSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchSpendCostsByMonth(ownProps.userType)(params, filters, pagination, sorting)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(SpendAnalyticsOverviewLocationsPage))
);
